import React, { useEffect, useRef, useState } from 'react';
import styled from "styled-components";

export default function VideoUploader({ file, onSelect, filePath }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const onChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;
    if (!f.type.startsWith('audio/')) {
      alert('오디오 파일만 업로드 가능합니다.');
      // 잘못된 선택 시 input 비우기
      if (inputRef.current) inputRef.current.value = '';
      onSelect(null);
      return;
    }
    onSelect(f);
  };
  const handleResetFrame = () => {
    onSelect(null);
  }
  // 동일 파일을 연속 선택해도 onChange가 뜨도록 클릭 시 value 리셋
  const onClickInput = (e) => {
    e.currentTarget.value = ''; // 매 클릭마다 초기화
  };

  // 미리보기 URL 관리
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      // 부모가 setFile(null) 했을 때 input도 비워 줌
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(()=> {
    if(filePath !== ''){
      setPreviewUrl(filePath);
      inputRef.current.value = '';
    }
  }, [filePath])

  const fileName = file ? file.name : previewUrl?.split('s3-oaple')[1];
  const fileSize = file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : '';

  return (
      <Wrapper>
        <UploadRow>
          <UploadLabel htmlFor="audioFileInput">
            <UploadIcon aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4M7 9l5-5 5 5"/>
                <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/>
              </svg>
            </UploadIcon>
            파일 선택
          </UploadLabel>
          <HiddenFileInput
              id="audioFileInput"
              ref={inputRef}
              type="file"
              accept="audio/mpeg,audio/*"
              onClick={onClickInput}
              onChange={onChange}
          />
          <FileNameText $empty={!fileName}>
            {fileName || '선택된 파일이 없습니다.'}
          </FileNameText>
          {file && (
            <ResetButton type="button" onClick={handleResetFrame} title="파일 삭제">
              {resetIcon}
            </ResetButton>
          )}
        </UploadRow>

        {previewUrl && (
          <PlayerWrap>
            <audio
                src={previewUrl}
                controls
                style={{ width: '100%' }}
            />
            {fileSize && <FileSizeText>{fileSize}</FileSizeText>}
          </PlayerWrap>
        )}
      </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

const UploadLabel = styled.label`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  background-color: var(--color-surface);
  color: #444;
  font-size: var(--font-size-14);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${({theme}) => theme.color.subColor};
    color: ${({theme}) => theme.color.subColor};
  }
`;

const UploadIcon = styled.span`
  display: inline-flex;
`;

const FileNameText = styled.span`
  overflow: hidden;
  flex: 1;
  min-width: 0;
  color: ${({$empty}) => ($empty ? '#aaa' : '#333')};
  font-size: var(--font-size-14);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResetButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #eee;
  border-radius: 6px;
  background: var(--color-surface);
  color: #888;
  cursor: pointer;

  &:hover {
    border-color: #f3c9c9;
    background-color: #fff5f5;
    color: #d9534f;
  }
`;

const PlayerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border: 1px solid #eee;
  border-radius: 6px;
  background-color: #fafafa;
`;

const FileSizeText = styled.small`
  align-self: flex-end;
  color: #999;
  font-size: var(--font-size-13);
`;

const resetIcon = <svg width="16" height="16" viewBox="0 0 21 21"><g fill="none" fillRule="evenodd" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" transform="matrix(0 1 1 0 2.5 2.5)"><path d="m3.98652376 1.07807068c-2.38377179 1.38514556-3.98652376 3.96636605-3.98652376 6.92192932 0 4.418278 3.581722 8 8 8s8-3.581722 8-8-3.581722-8-8-8"/><path d="m4 1v4h-4" transform="matrix(1 0 0 -1 0 6)"/></g></svg>
